import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { placeOrderNowRequest, placeOrderCartRequest, updateQuantityRequest, fetchCartRequest, clearCart, fetchCartCountRequest } from '../../modules/shop/actions';
import { message, QRCode, Modal } from 'antd';
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { SlLocationPin } from "react-icons/sl";
import { BsCheckCircleFill, BsCircle, BsInfo } from "react-icons/bs";
import { AlipayCircleFilled, WechatFilled } from '@ant-design/icons';
import { Skeleton, Spin } from 'antd';
import httpClient from '../../utils/HttpClient';
import { ORDERS_URL, RECEIVERS_URL } from '../../constants/Url';
import { stripHtml } from '../../utils/Utils';
import { useTranslation } from 'react-i18next';

export default function CheckoutPage() {
  const { items, total: cartTotal, loading: cartLoading, _id: cartId } = useSelector(state => state.shop.cart);
  const isPlacingOrder = useSelector(state => state.shop.orderInfo?.loading || false);
  const [orderLoading, setOrderLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [address, setAddress] = useState(location.state?.selectedAddress || null);
  const [loading, setLoading] = useState(false);

  const buyNowItem = location.state?.buyNowItem;
  const [localBuyNowItem, setLocalBuyNowItem] = useState(buyNowItem);

  const displayItems = localBuyNowItem ? [localBuyNowItem] : items;
  const displayTotal = localBuyNowItem ? (localBuyNowItem.unitPrice * localBuyNowItem.qty) : cartTotal;

  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponDiscountData, setCouponDiscountData] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const finalTotal = couponDiscountData?.discountValueStored ? displayTotal - couponDiscountData?.discountValueStored * displayTotal : displayTotal; ///Math.max(0, displayTotal - couponDiscount);

  const runTimer = useRef(null);
  const timer = useRef(null);
  const [paymentQrcode, setPaymentQrcode] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [checkPaymentStatusParam, setCheckPaymentStatusParam] = useState(null);
  const [showCloseModal, setShowCloseModal] = useState(false);

  useEffect(() => {
    return () => {
      setPaymentQrcode(null);
      setErrorMessage(null);
      runTimer.current = false;
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    }
  }, []);

  const handleClearCoupon = () => {
    setCouponDiscount(0);
    setCouponCode("");
    setCouponDiscountData(null)
    message.success(t('text_coupon_cancelled'));
  };

  const handleApplyCoupon = async (isManual = true) => {
    // if (!couponCode.trim()) {
    //   if (isManual === true) message.warning('text_input_coupon');
    //   return;
    // }
    if (isManual === true) setApplyingCoupon(true);
    try {
      const res = await httpClient.post('/coupons-scan-validate', {
        couponCode: couponCode.trim(),
        currentOrderTotal: displayTotal
      });
      const data = res?.data?.data || res?.data;
      const discount = data?.discountAmount || data?.discount || 0;
      if (discount > 0 && res?.data?.status === 200) {
        setCouponDiscount(discount);
        setCouponDiscountData(data)
        if (isManual === true) message.success(t('text_coupon_success'));
        // setCouponCode("");
      } else {
        if (isManual === true) message.error(t(res?.data?.message || 'text_coupon_invalid'));
        setCouponDiscount(0);
        setCouponDiscountData(null)
        setCouponCode("")
      }
    } catch (err) {
      if (isManual === true) message.error(t(err?.response?.data?.message || err?.message || 'text_coupon_failed'));
      setCouponDiscount(0);
      setCouponDiscountData(null)
      setCouponCode("")
    } finally {
      if (isManual === true) setApplyingCoupon(false);
    }
  };

  const cart = useSelector((state) => state.shop.cart);
  useEffect(() => {
    if (cart?.loading && couponCode && couponDiscount > 0) {
      handleApplyCoupon(false);
    }
  }, [cart?.loading]);

  useEffect(() => {
    if (!address) {
      fetchDefaultAddress();
    }
  }, []);

  const fetchDefaultAddress = async () => {
    setLoading(true);
    try {
      const res = await httpClient.get(`${RECEIVERS_URL}/default`);
      const a = res?.data?.data || res?.data;
      if (a && a._id) {
        setAddress({
          id: a._id,
          name: a.recipientName,
          phone: a.phone?.number || '',
          address: `${a.province || ''}${a.city || ''}${a.district || ''}${a.addressLine2 || ''}${a.addressLine1 || ''}`
        });
      }
    } catch (err) {
      console.error("Failed to fetch default address:", err);
    } finally {
      setLoading(false);
    }
  };

  const [paymentMethod, setPaymentMethod] = useState(location.state?.paymentMethod || 'alipay'); // 'alipay' or 'wechat'
  const [initialFetchDone, setInitialFetchDone] = useState(items && items.length > 0);

  useEffect(() => {
    if (!localBuyNowItem && (!items || items.length === 0)) {
      dispatch(fetchCartRequest());
    }
  }, [dispatch, localBuyNowItem]);

  useEffect(() => {
    if (cartLoading) {
      setInitialFetchDone(false);
    } else {
      setInitialFetchDone(true);
    }
  }, [cartLoading]);

  useEffect(() => {
    if (!localBuyNowItem && initialFetchDone && !cartLoading && (!items || items.length === 0)) {
      navigate('/cart');
    }
  }, [items, cartLoading, navigate, initialFetchDone, localBuyNowItem]);

  if (!localBuyNowItem && (cartLoading || !initialFetchDone) && (!items || items.length === 0)) {
    return (
      <div className="w-full max-w-[1263px] bg-[#f7f8fa] lg:bg-transparent flex flex-col relative mx-auto lg:my-10 lg:px-8 pt-4 lg:pt-0">
        <div className="hidden lg:block mb-8 px-4 lg:px-0">
          <Skeleton.Input active size="large" style={{ width: 150 }} />
        </div>
        <div className="px-4 lg:px-0 py-4 space-y-4 lg:space-y-6 flex-1 flex flex-col lg:flex-row lg:gap-8">
          <div className="flex-1 space-y-4 lg:space-y-6">
            <div className="bg-white rounded-2xl p-5 shadow-sm border-2 border-gray-100 flex items-center gap-4">
              <Skeleton.Avatar active size="large" shape="circle" />
              <div className="flex-1">
                <Skeleton active title={{ width: '40%' }} paragraph={{ rows: 1, width: '80%' }} />
              </div>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm border-2 border-gray-100 space-y-5">
              {[1, 2].map(i => (
                <div key={i} className="flex gap-4">
                  <Skeleton.Image active style={{ width: 96, height: 96 }} className="rounded-xl" />
                  <div className="flex-1 py-1">
                    <Skeleton active title={{ width: '70%' }} paragraph={{ rows: 1, width: '30%' }} />
                    <div className="mt-4"><Skeleton.Input active size="small" style={{ width: 60 }} /></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:w-[380px] shrink-0 space-y-4 lg:space-y-6">
            <div className="bg-white rounded-2xl p-5 shadow-sm border-2 border-gray-100">
              <Skeleton active paragraph={{ rows: 2 }} />
            </div>
            <div className="hidden lg:block bg-white rounded-2xl p-6 shadow-sm border-2 border-gray-100">
              <Skeleton active title={{ width: '100%' }} paragraph={{ rows: 0 }} />
              <div className="mt-6">
                <Skeleton.Button active block size="large" style={{ height: 50, borderRadius: 12 }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!displayItems || displayItems.length === 0) {
    return null;
  }

  const handleUpdateQuantity = (id, newQty, fallbackProductId) => {
    if (localBuyNowItem) {
      setLocalBuyNowItem({ ...localBuyNowItem, qty: newQty });
    } else {
      dispatch(updateQuantityRequest(id, newQty, fallbackProductId));
    }
  };

  const checkPaymentStatus = async (orderId, method, memo) => {
    if (!runTimer.current) return;
    setErrorMessage(null);
    try {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = null;
      const res = await httpClient.post(`${ORDERS_URL}/payment-query`, {
        method: method,
        orderId: orderId
      });
      if (res.data && res.data.status === 200) {
        const order = res.data.data || res.data;
        if (order.paymentStatus === "paid") {
          // Call payment API to mark as paid
          runTimer.current = false;
          message.success(t('Order placed successfully'));

          // Reset and sync cart state
          dispatch(clearCart());
          dispatch(fetchCartRequest());
          dispatch(fetchCartCountRequest());
          window.dispatchEvent(new Event('cart-updated'));

          navigate('/order-confirmation', { state: { orderId: order.orderId || order._id || order.id } });
          return;
        }
      } else {
        runTimer.current = false;
        setErrorMessage(t('text_failed_loading_order_detail'));
        return;
      }
      timer.current = setTimeout(() => {
        checkPaymentStatus(orderId, method, memo);
      }, 3000);
    } catch (err) {
      runTimer.current = false;
      console.error('Failed to check order status:', err);
      setErrorMessage(err.message || 'Error checking order status');
    }
  };

  const handlePayment = async () => {
    if (!address || !address.id) {
      message.warning(t('text_select_shipping_address'));
      return;
    }

    if (localBuyNowItem) {
      setOrderLoading(true);
      const orderData = {
        productId: localBuyNowItem.productNo || localBuyNowItem.productId,
        qty: localBuyNowItem.qty,
        receiverId: address.id,
        method: paymentMethod,
        memo: 'Please deliver between 9 AM to 5 PM.',
        currency: 'CNY',
        couponCode: couponDiscount > 0 ? couponCode : undefined
      };
      const res = await httpClient.post(`${ORDERS_URL}/now`, orderData);

      if (res.data && (res.status === 200 || res.status === 201 || res.data.status === 200)) {
        const order = res.data.data || res.data;
        const oId = order.orderId || order._id || order.id;

        var code_url = null;
        if (paymentMethod === 'alipay') {
          code_url = order?.orderinfo?.qrCode;
        } else if (paymentMethod === 'wechatpay') {
          code_url = order?.orderinfo?.code_url;
        }
        setPaymentQrcode(code_url);

        if (code_url) {
          runTimer.current = true;
          setCheckPaymentStatusParam({ orderId: oId, method: paymentMethod, memo: orderData.memo });
          checkPaymentStatus(oId, paymentMethod, orderData.memo);
        }
      } else {
        console.error('Order creation failed:', res.data);
        message.error(res.data?.message || 'Error placing order');
      }
      setOrderLoading(false);
      // dispatch(placeOrderNowRequest({ orderData, navigate }));
    } else {
      setOrderLoading(true);
      const orderData = {
        cartId: cartId,
        cartItems: displayItems.map(item => item._id),
        receiverId: address.id,
        method: paymentMethod,
        memo: 'Please deliver between 9 AM to 5 PM.',
        currency: 'CNY',
        couponCode: couponDiscount > 0 ? couponCode : undefined
      };
      const res = await httpClient.post(`${ORDERS_URL}/checkout`, orderData);

      if (res.data && (res.status === 200 || res.status === 201 || res.data.status === 200)) {
        const order = res.data.data || res.data;
        const oId = order.orderId || order._id || order.id;

        var code_url = null;
        if (paymentMethod === 'alipay') {
          code_url = order?.orderinfo?.qrCode;
        } else if (paymentMethod === 'wechatpay') {
          code_url = order?.orderinfo?.code_url;
        }
        setPaymentQrcode(code_url);

        if (code_url) {
          runTimer.current = true;
          setCheckPaymentStatusParam({ orderId: oId, method: paymentMethod, memo: orderData.memo });
          checkPaymentStatus(oId, paymentMethod, orderData.memo);
        }
      } else {
        console.error('Order creation failed:', res.data);
        message.error(res.data?.message || 'Error placing order');
      }
      setOrderLoading(false);
      // dispatch(placeOrderCartRequest({ orderData, navigate }));
    }
  };

  let couponDiscounts = couponDiscountData?.discountValueStored ? couponDiscountData?.discountValueStored * displayTotal : 0;

  return (
    <div className="w-full max-w-[1263px] bg-[#f7f8fa] lg:bg-transparent flex flex-col relative mx-auto lg:my-10 lg:px-8">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md flex items-center justify-center relative py-4 px-4 shadow-sm z-20 sticky top-0 transition-all duration-300 lg:hidden">
        <button
          onClick={() => navigate(-1)}
          className="absolute left-4 text-xl text-gray-800 hover:scale-110 hover:text-[#e80d44] transition-all duration-300"
        >
          <IoIosArrowBack />
        </button>
        <h1 className="text-lg font-medium text-gray-900 tracking-wider">{t("text_confirm_order")}</h1>
      </div>

      <div className="hidden lg:block mb-8">
        <h1 className="text-3xl font-bold text-gray-900 tracking-wider">{t("text_confirm_order")}</h1>
      </div>

      <div className="px-4 lg:px-0 py-4 flex-1 flex flex-col lg:flex-row gap-4 lg:gap-8 pb-32 lg:pb-10">
        <div className="flex-1 space-y-4 lg:space-y-6">
          <div
            onClick={() => navigate('/address-list', { state: { ...location.state, buyNowItem: localBuyNowItem, paymentMethod, fromCheckout: true } })}
            className="bg-white rounded-2xl p-5 border-2 border-gray-100 flex items-center justify-between cursor-pointer"
          >
            <div className="flex items-start gap-4">
              <div className="bg-[#fff0f4] p-2.5 rounded-full mt-0.5">
                <SlLocationPin className="text-[#e80d44] text-xl shrink-0" />
              </div>
              {loading ? (
                <div className="flex-1 w-full pt-1">
                  <Skeleton active title={{ width: '40%' }} paragraph={{ rows: 1, width: '80%' }} />
                </div>
              ) : address ? (
                <div className="flex flex-col">
                  <div className="flex items-center gap-4 mb-1.5">
                    <span className="text-base font-semibold text-gray-900">{address.name}</span>
                    <span className="text-sm text-gray-600 font-medium tracking-wide">{address.phone}</span>
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed break-words line-clamp-2 pr-2">{address.address}</p>
                </div>
              ) : (
                <div className="flex flex-col justify-center h-[52px]">
                  <span className="text-base font-semibold text-gray-900">{t("text_select_shipping_address")}</span>
                </div>
              )}
            </div>
            <IoIosArrowForward className="text-gray-300 text-xl shrink-0 group-hover:text-[#e80d44] transition-colors duration-300" />
          </div>

          {/* Product List */}
          <div className="bg-white rounded-2xl p-5 shadow-[0_4px_20px_rgba(0,0,0,0.02)] border-2 border-gray-100 space-y-5">
            {displayItems.map((item, index) => (
              <div key={index} className="flex gap-4 group">
                <div className="w-24 h-24 rounded-xl p-1.5 shrink-0 overflow-hidden relative">
                  <img src={item.productImages?.[0] || ""} alt={item.productName} className="w-full h-full object-cover rounded-lg bg-white mix-blend-multiply transition-transform duration-500" />
                </div>
                <div className="flex-1 flex flex-col justify-between py-1">
                  <div>
                    <h3 className="text-sm text-gray-900 font-semibold leading-relaxed tracking-wide">{item.productName}</h3>
                    {/* <p className="text-xs text-gray-400 mt-1 break-words whitespace-normal">{stripHtml(item && item?.description) || ''}</p> */}
                  </div>
                  <div className="flex justify-between items-center mt-3">
                    <span className="text-[#e80d44] text-lg font-bold tracking-tight">¥{item.unitPrice?.toFixed(2)}</span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => item.qty > 1 && handleUpdateQuantity(item._id, item.qty - 1, item.productId || item.productNo || item.product?._id || item.product)}
                        className={`w-8 h-8 rounded border flex items-center justify-center ${item.qty > 1 ? 'hover:bg-gray-50 cursor-pointer' : 'text-gray-300 cursor-not-allowed bg-gray-50/50'}`}
                        disabled={item.qty <= 1}
                      >
                        -
                      </button>
                      <span className="font-semibold">{item.qty}</span>
                      <button
                        onClick={() => handleUpdateQuantity(item._id, item.qty + 1, item.productId || item.productNo || item.product?._id || item.product)}
                        className="w-8 h-8 rounded border flex items-center justify-center hover:bg-gray-50"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column on Desktop */}
        <div className="lg:w-[380px] shrink-0 space-y-4 lg:space-y-6">

          {/* Payment Methods */}
          <div className="bg-white rounded-2xl px-5 shadow-[0_4px_20px_rgba(0,0,0,0.02)] border-2 border-gray-100">
            <div
              onClick={() => setPaymentMethod('alipay')}
              className="flex items-center justify-between py-4 border-b border-gray-50 cursor-pointer group hover:bg-gray-50/50 transition-colors -mx-5 px-5 first:rounded-t-2xl"
            >
              <div className="flex items-center gap-4">
                <AlipayCircleFilled className="text-[#1677ff] text-[28px]" />
                <span className="text-sm text-gray-800 font-medium tracking-wide">{t("text_alipay_payment")}</span>
              </div>
              {paymentMethod === 'alipay' ? (
                <BsCheckCircleFill className="text-[#e80d44] text-xl shadow-sm rounded-full" />
              ) : (
                <BsCircle className="text-gray-200 text-xl group-hover:border-gray-300 transition-colors" />
              )}
            </div>

            <div
              onClick={() => setPaymentMethod('wechatpay')}
              className="flex items-center justify-between py-4 cursor-pointer group hover:bg-gray-50/50 transition-colors -mx-5 px-5 last:rounded-b-2xl"
            >
              <div className="flex items-center gap-4">
                <WechatFilled className="text-[#09b83e] text-[28px]" />
                <span className="text-sm text-gray-800 font-medium tracking-wide">{t("text_wechat_payment")}</span>
              </div>
              {paymentMethod === 'wechatpay' ? (
                <BsCheckCircleFill className="text-[#e80d44] text-xl shadow-sm rounded-full" />
              ) : (
                <BsCircle className="text-gray-200 text-xl group-hover:border-gray-300 transition-colors" />
              )}
            </div>
          </div>

          {/* Coupon Section hidden for product purchase */}

          {/* Payment Summary Box (Desktop and Tablet, mobile uses sticky bar) */}
          <div className="hidden md:block bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.02)] border-2 border-gray-100">
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500">{t("text_product_total")}</span>
                <span className="text-gray-900 font-medium">¥{displayTotal?.toFixed(2)}</span>
              </div>
              <div className="pt-3 border-t border-gray-100 flex justify-between items-center">
                <span className="text-gray-600 font-medium">{t("text_tobepaid_amount")}</span>
                <span className="text-[#e80d44] text-3xl font-bold tracking-tight">¥{displayTotal?.toFixed(2)}</span>
              </div>
            </div>
            <button
              onClick={handlePayment}
              disabled={(isPlacingOrder || orderLoading)}
              className={`mt-6 w-full bg-gradient-to-r from-[#e80d44] to-[#ff3b68] text-white py-2.5 rounded-xl font-bold text-lg transition-all duration-300 flex justify-center items-center gap-2 ${isPlacingOrder || orderLoading ? 'opacity-70 cursor-not-allowed' : 'active:scale-[0.98]'}`}
            >
              {(isPlacingOrder || orderLoading) && <Spin size="small" />} {t("text_pay_now")}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Sticky Payment Bar (Mobile Only) */}
      <div className="fixed md:hidden bottom-0 left-0 right-0 w-full bg-white/90 backdrop-blur-md border-t-2 border-black/5 flex items-center justify-between p-4 z-20 shadow-[0_-8px_30px_-5px_rgba(0,0,0,0.08)]">
        <div className="pl-2 flex flex-col gap-0.5">
          <div className="flex items-baseline gap-1">
            <span className="text-sm text-gray-500 font-medium">{t("text_to_be_paid")}</span>
            <span className="text-[#e80d44] text-2xl font-bold tracking-tight">¥{displayTotal?.toFixed(2)}</span>
          </div>
        </div>
        <button
          onClick={handlePayment}
          disabled={isPlacingOrder || orderLoading}
          className={`bg-gradient-to-r from-[#e80d44] to-[#ff3b68] text-white px-10 py-3 rounded-full font-semibold text-sm transition-all duration-300 flex justify-center items-center gap-2 ${(isPlacingOrder || orderLoading) ? 'opacity-70 cursor-not-allowed' : 'hover:scale-105 active:scale-95'}`}
        >
          {(isPlacingOrder || orderLoading) && <Spin size="small" />} {t("text_pay_now")}
        </button>
      </div>

      {/* Payment QR Code Modal */}
      <Modal
        open={!!paymentQrcode}
        onCancel={() => {
          setShowCloseModal(true);
        }}
        footer={null}
        centered
        width={450}
        title={<div className="text-center font-bold text-base text-gray-800 pt-1">{t("text_tobepaid_qrcode")}</div>}
        styles={{
          body: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px 16px 32px 16px',
          }
        }}
      >
          <div className="flex flex-col items-center justify-center p-2">
            <QRCode
              value={paymentQrcode}
              size={220}
              icon={paymentMethod === 'wechatpay' ? "https://ts4.tc.mm.bing.net/th/id/ODF.BvtHqZTl6qLypPDIASUGoA?w=32&h=32&qlt=97&pcl=fffffa&o=6&pid=1.2" : "https://ts1.tc.mm.bing.net/th/id/ODF.tFQwCWKfvMeJYOFxHknauw?w=32&h=32&qlt=90&pcl=fffffa&o=6&pid=1.2"}
              iconSize={{
                width: 32,
                height: 32,
              }}
            />
          </div>
          <div style={{marginTop: 10, marginBottom: errorMessage ? 10 : 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5}}>
            <div className="bg-[#fff0f4] p-0.5 rounded-full">
              <BsInfo className="text-[#e80d44] text-xl shrink-0" />
            </div>
            <span className="text-[#e80d44] text-sm font-medium">{t("text_qrcode_desc")}</span>
          </div>
          {errorMessage ? <div className="flex flex-col items-center justify-center w-full">
            <span style={{color: 'red'}} className="text-sm text-gray-500 font-medium">{errorMessage}</span>

            <button
              onClick={() => {
                checkPaymentStatus(checkPaymentStatusParam.orderId, checkPaymentStatusParam.method, checkPaymentStatusParam.memo);
              }}
              style={{width: '100%'}}
              className={`rounded-xl bg-gradient-to-r mt-4 from-[#e80d44] to-[#ff3b68] text-white px-10 py-3 font-semibold text-sm transition-all duration-300 flex justify-center items-center gap-2 ${(isPlacingOrder || orderLoading) ? 'opacity-70 cursor-not-allowed' : 'hover:scale-105 active:scale-95'}`}
            >
              {t("text_retry")}
            </button>
          </div> : null}
          <Modal
            open={showCloseModal}
            onCancel={() => {
              setShowCloseModal(false);
            }}
            closeable={true}
            footer={null}
            centered
            width={400}
            title={null}
            styles={{
              body: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px 16px 32px 16px',
              }
            }}
          >
            <div className="flex flex-col items-center justify-center p-2">
              <div style={{padding: 12}} className="bg-[#fff0f4] rounded-full">
                <BsInfo size={36} color="#e80d44" />
              </div>
              <span className="text-sm shrink-0 font-medium mt-4">{t("text_qrcode_warning")}</span>
              <button
                onClick={() => {
                  setShowCloseModal(false);
                  setTimeout(() => {
                    setPaymentQrcode(null);
                    setErrorMessage(null);
                    setCheckPaymentStatusParam(null);
                    runTimer.current = false;
                    if (timer.current) {
                      clearTimeout(timer.current);
                      timer.current = null;
                    }
                    window.history.back();
                  }, 100);
                }}
                style={{width: '100%'}}
                className={`rounded-xl bg-gradient-to-r mt-4 from-[#e80d44] to-[#ff3b68] text-white px-10 py-3 font-semibold text-sm transition-all duration-300 flex justify-center items-center gap-2 ${(isPlacingOrder || orderLoading) ? 'opacity-70 cursor-not-allowed' : 'hover:scale-105 active:scale-95'}`}
              >
                {t("text_qrcode_confirm")}
              </button>
            </div>
          </Modal>
      </Modal>
    </div>
  );
}
